import { addDateDuration, toISOString, toPlainDate } from '@/utils/temporalUtils';

enum SeasonPrefix {
  SS = 'SS',
  FW = 'FW',
}

const DISTRIBUTOR_EXGO_WINDOW_MONTHS: Record<
  SeasonPrefix,
  { earliestMonth: number; latestMonth: number; latestYearOffset: number }
> = {
  [SeasonPrefix.SS]: { earliestMonth: 8, latestMonth: 3, latestYearOffset: 1 },
  [SeasonPrefix.FW]: { earliestMonth: 3, latestMonth: 8, latestYearOffset: 0 },
};

/**
 * Derives the allowed Exgo date window for DIST accounts based on the season type.
 *
 * Rules:
 *   SS season → Aug 1 of the season year – Mar 1 of the following year
 *   FW season → Mar 1 of the season year – Aug 1 of the same year
 *
 * The season year is derived from `seasonStartDate`.
 * Returns `null` when the season type cannot be determined or the date is invalid.
 */
export function getDistributorExgoWindow(
  seasonName: string | undefined,
  seasonStartDate: string,
): { earliest: string; latest: string } | null {
  const seasonType = getDistributorSeasonType(seasonName);

  if (!seasonType) {
    return null;
  }

  try {
    const seasonStart = toPlainDate(seasonStartDate);
    const { earliestMonth, latestMonth, latestYearOffset } = DISTRIBUTOR_EXGO_WINDOW_MONTHS[seasonType];
    const seasonYearStart = seasonStart.with({ month: earliestMonth, day: 1 });
    const seasonYearEnd = addDateDuration(seasonStart.with({ month: latestMonth, day: 1 }), {
      years: latestYearOffset,
    });

    return {
      earliest: toISOString(seasonYearStart),
      latest: toISOString(seasonYearEnd),
    };
  } catch {
    return null;
  }
}

function getDistributorSeasonType(seasonName: string | undefined): SeasonPrefix | null {
  if (!seasonName) {
    return null;
  }

  const seasonType = seasonName.slice(0, 2).toUpperCase();

  return seasonType === SeasonPrefix.SS || seasonType === SeasonPrefix.FW ? (seasonType as SeasonPrefix) : null;
}
